import React, { useState, useEffect } from "react";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import { FaStar, FaCrown, FaShieldAlt, FaMedal } from "react-icons/fa";
import { toast } from "react-toastify";
import subscriptionApi from "../api/subscriptionApi";
import jwtTokenService from "../services/jwtTokenService";
import { RAZORPAY_CHECKOUT_CONFIG } from "../config/razorpay.config";

// Master list of all possible features/facilities
const allFeatures = [
  "Basic booking access",
  "Email notifications",
  "Standard customer support",
  "Basic profile management",
  "10% discount on room bookings",
  "5% discount on restaurant bookings",
  "Priority customer support",
  "Early check-in (subject to availability)",
  "Loyalty points on every booking",
  "Free WiFi upgrade",
  "Monthly exclusive offers",
  "20% discount on room bookings",
  "15% discount on restaurant bookings",
  "Priority booking access",
  "Free room upgrades (subject to availability)",
  "Complimentary breakfast",
  "Late checkout until 2 PM",
  "30% discount on room bookings",
  "25% discount on restaurant bookings",
  "Guaranteed room upgrades",
  "Complimentary breakfast + dinner",
  "Free airport pickup/drop (within city)",
  "Personal butler service",
  "No discounts",
  "No priority booking",
  "Limited support hours",
];

// Plans with features mapped to master list
const plans = [
  {
    name: "Basic",
    price: "₹0",
    description: "Essential features for casual travelers",
    icon: (
      <FaShieldAlt
        style={{
          fontSize: "2.5rem",
          color: "#94A3B8",
          margin: "0 auto",
          display: "block",
        }}
      />
    ),
    features: {
      "Basic booking access": true,
      "Email notifications": true,
      "Standard customer support": true,
      "Basic profile management": true,
      "No discounts": false,
      "No priority booking": false,
      "Limited support hours": false,
    },
  },
  {
    name: "Silver",
    price: "₹2,999",
    description: "Perfect for frequent travelers",
    icon: (
      <FaStar
        style={{
          fontSize: "2.5rem",
          color: "#C0C0C0",
          margin: "0 auto",
          display: "block",
        }}
      />
    ),
    badge: "Most Popular",
    features: {
      "10% discount on room bookings": true,
      "5% discount on restaurant bookings": true,
      "Priority customer support": true,
      "Early check-in (subject to availability)": true,
      "Loyalty points on every booking": true,
      "Free WiFi upgrade": true,
      "Monthly exclusive offers": true,
    },
  },
  {
    name: "Gold",
    price: "₹5,999",
    description: "Premium experience for luxury seekers",
    icon: (
      <FaMedal
        style={{
          fontSize: "2.5rem",
          color: "#FFD700",
          margin: "0 auto",
          display: "block",
        }}
      />
    ),
    features: {
      "20% discount on room bookings": true,
      "15% discount on restaurant bookings": true,
      "Priority booking access": true,
      "Free room upgrades (subject to availability)": true,
      "Complimentary breakfast": true,
      "Late checkout until 2 PM": true,
    },
  },
  {
    name: "Platinum",
    price: "₹9,999",
    description: "Ultimate luxury and personalized service",
    icon: (
      <FaCrown
        style={{
          fontSize: "2.5rem",
          color: "#8B5CF6",
          margin: "0 auto",
          display: "block",
        }}
      />
    ),
    badge: "Premium",
    features: {
      "30% discount on room bookings": true,
      "25% discount on restaurant bookings": true,
      "Guaranteed room upgrades": true,
      "Complimentary breakfast + dinner": true,
      "Free airport pickup/drop (within city)": true,
      "Personal butler service": true,
    },
  },
];

// Custom confirmation modal
const UpgradeConfirmModal = ({ plan, amount, onConfirm, onCancel, loading }) => {
  const isPremium = plan?.badge === "Premium";
  const gradient = isPremium
    ? "linear-gradient(135deg, #8B5CF6, #A78BFA)"
    : "linear-gradient(135deg, #EE2E24, #FFC400)";

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        animation: "fadeIn 0.2s ease",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: "white", borderRadius: "20px",
          padding: "36px 32px", maxWidth: "420px", width: "100%",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
          animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)",
          textAlign: "center",
        }}
      >
        {/* Plan icon & gradient bar */}
        <div
          style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: gradient, margin: "0 auto 16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: isPremium
              ? "0 8px 24px rgba(139,92,246,0.35)"
              : "0 8px 24px rgba(238,46,36,0.3)",
          }}
        >
          {plan?.icon && React.cloneElement(plan.icon, {
            style: { fontSize: "1.8rem", color: "white", margin: 0 }
          })}
        </div>

        <h2 style={{ fontSize: "1.4rem", fontWeight: "800", color: "#1E293B", marginBottom: "8px" }}>
          Upgrade to {plan?.name}?
        </h2>
        <p style={{ color: "#64748B", fontSize: "0.95rem", marginBottom: "24px", lineHeight: "1.5" }}>
          You'll be charged{" "}
          <span style={{ color: "#EE2E24", fontWeight: "700", fontSize: "1.05rem" }}>
            ₹{amount?.toLocaleString("en-IN")}
          </span>
          {" "}per year for the <strong>{plan?.name}</strong> plan.
          <br />
          <span style={{ fontSize: "0.85rem", color: "#94A3B8" }}>Your current plan will be cancelled.</span>
        </p>

        {/* Feature highlights */}
        <div
          style={{
            background: "#F8FAFC", borderRadius: "12px",
            padding: "14px 18px", marginBottom: "24px", textAlign: "left",
          }}
        >
          {Object.keys(plan?.features || {}).slice(0, 3).map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
              <BsCheckCircleFill style={{ color: "#22C55E", flexShrink: 0 }} />
              <span style={{ fontSize: "0.85rem", color: "#374151", fontWeight: "500" }}>{f}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1, padding: "12px 0", borderRadius: "12px",
              border: "2px solid #E2E8F0", background: "white",
              color: "#64748B", fontWeight: "600", fontSize: "0.95rem",
              cursor: "pointer", transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F1F5F9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1, padding: "12px 0", borderRadius: "12px",
              background: loading ? "#CBD5E1" : gradient,
              color: "white", fontWeight: "700", fontSize: "0.95rem",
              border: "none", cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 4px 15px rgba(238,46,36,0.3)",
              transition: "all 0.2s",
            }}
          >
            {loading ? "Processing…" : "Confirm Upgrade"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
    </div>
  );
};

const Membership = () => {
  const [expandedPlans, setExpandedPlans] = useState(
    Array(plans.length).fill(false)
  );
  const [planPrices, setPlanPrices] = useState({ SILVER: 2999, GOLD: 5999, PLATINUM: 9999 });
  const [currentPlan, setCurrentPlan] = useState("BASIC");
  const [upgrading, setUpgrading] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { plan, amount }
  const visibleCount = 6;

  const fetchCurrentPlan = (userId) => {
    subscriptionApi.getCurrentPlan(userId)
      .then((result) => {
        const planType = result?.data?.planType || result?.data?.membershipTier || "BASIC";
        setCurrentPlan(planType.toUpperCase());
      })
      .catch(() => {});
  };

  useEffect(() => {
    subscriptionApi.getPlanPrices()
      .then((result) => {
        const prices = result?.data || result;
        if (prices && (prices.SILVER || prices.GOLD || prices.PLATINUM)) {
          setPlanPrices(prices);
        }
      })
      .catch(() => {});

    const user = jwtTokenService.getUser();
    const userId = user?.id || user?.user_id || user?.userId;
    if (userId) {
      fetchCurrentPlan(userId);
    }
  }, []);

  const handleUpgrade = (plan) => {
    const user = jwtTokenService.getUser();
    const userId = user?.id || user?.user_id || user?.userId;
    if (!userId) {
      toast.error("Please log in to upgrade your plan.");
      return;
    }
    const planKey = plan.name.toUpperCase();
    const amount = planPrices[planKey];
    setConfirmModal({ plan, amount, userId, planKey });
  };

  const confirmUpgrade = async () => {
    if (!confirmModal) return;
    const { plan, amount, userId, planKey } = confirmModal;

    // Close the confirmation modal and enter loading state
    setConfirmModal(null);
    setUpgrading(plan.name);

    try {
      if (!window.Razorpay) {
        throw new Error("Payment system not loaded. Please refresh and try again.");
      }

      // Step 1: Create Razorpay order on backend
      const orderResponse = await subscriptionApi.createPaymentOrder(userId, plan.name, amount);
      if (!orderResponse.success) {
        throw new Error(orderResponse.error || "Failed to create payment order");
      }

      const { orderId, amount: razorpayAmount, currency, razorpayKey } = orderResponse.data;

      // Step 2: Open Razorpay checkout
      const options = {
        key: razorpayKey,
        amount: razorpayAmount,
        currency: currency || "INR",
        name: "Trivana Hospitality",
        description: `${plan.name} Membership Plan – 1 Year`,
        order_id: orderId,
        theme: { color: plan.badge === "Premium" ? "#8B5CF6" : "#EE2E24" },
        prefill: (() => {
          try {
            const u = JSON.parse(localStorage.getItem("trivana_user") || "{}");
            return {
              name:    u.name    || u.fullName || "",
              email:   u.email   || "",
              contact: u.phone   || u.mobile   || "",
            };
          } catch { return { name: "", email: "", contact: "" }; }
        })(),
        notes: { plan_name: plan.name, user_id: String(userId) },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled.");
            setUpgrading(null);
          },
        },
        ...RAZORPAY_CHECKOUT_CONFIG,
        handler: async (paymentResponse) => {
          // Step 3: Verify & activate plan on backend
          try {
            const upgradeData = {
              planType: planKey,
              paymentMethod: "RAZORPAY",
              transactionId: paymentResponse.razorpay_payment_id,
              amount: amount,
              currency: "INR",
              razorpayOrderId: paymentResponse.razorpay_order_id,
              razorpaySignature: paymentResponse.razorpay_signature,
            };

            const upgradeResult = await subscriptionApi.upgradePlan(userId, upgradeData);
            if (upgradeResult.success) {
              toast.success(`🎉 Successfully upgraded to ${plan.name} plan!`);
              setCurrentPlan(planKey);
              fetchCurrentPlan(userId);
              // Notify other parts of the app (e.g. ProfilePage)
              window.dispatchEvent(new CustomEvent("membershipChanged", { detail: { newPlan: planKey, userId } }));
            } else {
              toast.error(upgradeResult.error || "Plan activation failed. Contact support.");
            }
          } catch (err) {
            toast.error("Payment received but activation failed. Please contact support.");
          } finally {
            setUpgrading(null);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error?.description || "Unknown error"}`);
        setUpgrading(null);
      });
      rzp.open();

    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
      setUpgrading(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        paddingTop: "80px",
      }}
    >
      {/* Custom upgrade confirmation modal */}
      {confirmModal && (
        <UpgradeConfirmModal
          plan={confirmModal.plan}
          amount={confirmModal.amount}
          loading={upgrading === confirmModal.plan.name}
          onConfirm={confirmUpgrade}
          onCancel={() => { if (!upgrading) setConfirmModal(null); }}
        />
      )}
      <header
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          padding: "45px 0",
          marginBottom: "32px",
        }}
      >
        <div className="container mx-auto text-center">
          <h1
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
              fontWeight: "800",
              color: "white",
              marginBottom: "8px",
              letterSpacing: "-1px",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Choose Your Membership Plan
          </h1>
          <p
            style={{
              color: "white",
              fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
              opacity: 0.95,
              fontWeight: "500",
            }}
          >
            Unlock exclusive benefits and save more on your bookings
          </p>
        </div>
      </header>
      <main className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, idx) => {
            const isCurrentPlan = plan.name.toUpperCase() === currentPlan;
            const featureItems = allFeatures
              .map((feature, i) => {
                const included = plan.features[feature] === true;
                if (
                  [
                    "No discounts",
                    "No priority booking",
                    "Limited support hours",
                  ].includes(feature) &&
                  plan.name !== "Basic"
                )
                  return null;
                if (!included && plan.name !== "Basic") return null;
                return (
                  <li
                    key={i}
                    className="flex items-center gap-2"
                    style={{ fontSize: "0.85rem", marginBottom: "6px" }}
                  >
                    {included ? (
                      <BsCheckCircleFill
                        style={{
                          color: "#22C55E",
                          fontSize: "1.1rem",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <BsXCircleFill
                        style={{
                          color: "#EF4444",
                          fontSize: "1.1rem",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        color: included ? "#2E2E2E" : "#94A3B8",
                        textDecoration: included ? "none" : "line-through",
                        fontWeight: "500",
                      }}
                    >
                      {feature}
                    </span>
                  </li>
                );
              })
              .filter(Boolean);
            return (
              <div
                key={idx}
                className="membership-card"
                style={{
                  background: "white",
                  borderRadius: "14px",
                  boxShadow: isCurrentPlan
                    ? "0 8px 30px rgba(238, 46, 36, 0.15)"
                    : "0 4px 20px rgba(0,0,0,0.08)",
                  padding: "28px 20px",
                  position: "relative",
                  border: isCurrentPlan
                    ? "2px solid #22C55E"
                    : "2px solid #F1F5F9",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = isCurrentPlan
                    ? "0 20px 50px rgba(238, 46, 36, 0.25)"
                    : plan.badge === "Most Popular"
                    ? "0 20px 50px rgba(238, 46, 36, 0.2)"
                    : plan.badge === "Premium"
                    ? "0 20px 50px rgba(139, 92, 246, 0.2)"
                    : "0 15px 40px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = isCurrentPlan
                    ? "#22C55E"
                    : plan.badge === "Most Popular"
                    ? "#EE2E24"
                    : plan.badge === "Premium"
                    ? "#8B5CF6"
                    : "#E5E7EB";

                  // Animate icon
                  const iconDiv = e.currentTarget.querySelector(
                    '[style*="marginBottom"]'
                  );
                  if (iconDiv) {
                    iconDiv.style.transform = "scale(1.15) rotate(5deg)";
                    iconDiv.style.transition =
                      "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = isCurrentPlan
                    ? "0 8px 30px rgba(238, 46, 36, 0.15)"
                    : "0 4px 20px rgba(0,0,0,0.08)";
                  e.currentTarget.style.borderColor = isCurrentPlan

                  // Reset icon
                  const iconDiv = e.currentTarget.querySelector(
                    '[style*="marginBottom"]'
                  );
                  if (iconDiv) {
                    iconDiv.style.transform = "scale(1) rotate(0deg)";
                  }
                }}
              >
                {plan.badge && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-14px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      padding: "5px 16px",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      background:
                        plan.badge === "Most Popular"
                          ? "linear-gradient(135deg, #EE2E24, #FFC400)"
                          : "linear-gradient(135deg, #8B5CF6, #A78BFA)",
                      color: "white",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    {plan.badge}
                  </span>
                )}
                {isCurrentPlan && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-14px",
                      right: "16px",
                      padding: "5px 12px",
                      borderRadius: "20px",
                      background: "#22C55E",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      boxShadow: "0 4px 12px rgba(34, 197, 94, 0.3)",
                    }}
                  >
                    Current Plan
                  </span>
                )}
                <div
                  style={{
                    marginBottom: "14px",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {plan.icon}
                </div>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "800",
                    color: "#2E2E2E",
                    marginBottom: "6px",
                    textAlign: "center",
                    letterSpacing: "-0.6px",
                  }}
                >
                  {plan.name}
                </h2>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "800",
                    color: "#EE2E24",
                    marginBottom: "6px",
                    textAlign: "center",
                    letterSpacing: "-0.8px",
                  }}
                >
                  {plan.name === "Basic"
                    ? "₹0"
                    : plan.name === "Silver"
                    ? `₹${(planPrices.SILVER || 2999).toLocaleString("en-IN")}`
                    : plan.name === "Gold"
                    ? `₹${(planPrices.GOLD || 5999).toLocaleString("en-IN")}`
                    : `₹${(planPrices.PLATINUM || 9999).toLocaleString("en-IN")}`}
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#64748B",
                    }}
                  >
                    {plan.name !== "Basic" && "/per year"}
                  </span>
                </div>
                <p
                  style={{
                    color: "#64748B",
                    marginBottom: "16px",
                    textAlign: "center",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    lineHeight: "1.4",
                  }}
                >
                  {plan.description}
                </p>
                <ul style={{ marginBottom: "8px" }}>
                  {expandedPlans[idx]
                    ? featureItems
                    : featureItems.slice(0, visibleCount)}
                </ul>
                {featureItems.length > visibleCount && (
                  <button
                    style={{
                      color: "#EE2E24",
                      textDecoration: "underline",
                      fontSize: "0.85rem",
                      marginTop: "8px",
                      fontWeight: "600",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      transition: "color 0.2s ease",
                    }}
                    onClick={() => {
                      const updated = [...expandedPlans];
                      updated[idx] = !updated[idx];
                      setExpandedPlans(updated);
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#D82820")}
                    onMouseLeave={(e) => (e.target.style.color = "#EE2E24")}
                  >
                    {expandedPlans[idx] ? "View Less" : "View More"}
                  </button>
                )}
                {/* Upgrade / Current Plan button */}
                <div style={{ marginTop: "20px" }}>
                  {isCurrentPlan ? (
                    <div
                      style={{
                        width: "100%",
                        padding: "11px 0",
                        borderRadius: "10px",
                        background: "#F0FDF4",
                        border: "2px solid #22C55E",
                        color: "#16A34A",
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        textAlign: "center",
                      }}
                    >
                      ✓ Current Plan
                    </div>
                  ) : plan.name === "Basic" ? null : (
                    <button
                      disabled={upgrading === plan.name}
                      onClick={() => handleUpgrade(plan)}
                      style={{
                        width: "100%",
                        padding: "11px 0",
                        borderRadius: "10px",
                        background:
                          upgrading === plan.name
                            ? "#CBD5E1"
                            : plan.badge === "Premium"
                            ? "linear-gradient(135deg, #8B5CF6, #A78BFA)"
                            : "linear-gradient(135deg, #EE2E24, #FFC400)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        border: "none",
                        cursor: upgrading === plan.name ? "not-allowed" : "pointer",
                        boxShadow: upgrading === plan.name
                          ? "none"
                          : "0 4px 15px rgba(238,46,36,0.3)",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {upgrading === plan.name
                        ? "Processing..."
                        : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default Membership;
